import { BehaviorSubject, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { ColorSchemes } from 'src/styles/color-scheme.type';
import { MapHlp } from 'src/lib/fliko/helpers/map.helper';
import { ToString } from 'src/lib/fliko/helpers/toString.helper';
import { GenericNote, NoteType } from 'src/lib/fliko/models/notes/notes.data';
import { ContextDataService } from 'src/app/view/user-context/services/context.data-service';
import { NoteService } from 'src/app/view/users/services/note.service';
import { ErrorHandlingService } from '../../error-handling/error-handling.service';
import { DialogService } from '../../services/dialog.service';
import { LocalizationService } from '../../services/localization.service';
import { PushIdService } from '../../services/pushId.service';
import { SnackBarService } from '../../services/snackBar.service';

@Component({
  selector: 'app-notes[type][refId]',
  templateUrl: './notes.component.html',
  styleUrls: ['./notes.component.scss']
})
export class NotesComponent implements OnInit, OnChanges, OnDestroy {
  @Input() type!: NoteType;
  @Input() refId!: string;
  @Input() disableEdit = false;
  @Input() hideControls = false;
  @Input() contentOnly = false;
  @Input() emptyTextKey?: string;

  public refId$ = new BehaviorSubject<string>('');

  public createMode = false;
  public changedNoteIndex = -1;
  public notes: GenericNote[] = [];

  public noteTitleControl: FormControl;
  public noteDescriptionControl: FormControl;

  public currentUserId: string;
  private usersNameMap = new Map<string, string>();

  private trans = (x: string) => this.ls.translate(x);

  private subscription$ = new Subscription();

  NoteType = NoteType;
  constructor(
    private fb: FormBuilder,
    private errorHandlingService: ErrorHandlingService,
    private noteService: NoteService,
    private ctxDS: ContextDataService,
    private dialogService: DialogService,
    private snackBarService: SnackBarService,
    private ls: LocalizationService,
    private pushId: PushIdService
  ) {
    this.noteTitleControl = this.fb.control(null, Validators.compose([Validators.required, Validators.maxLength(100)]));
    this.noteDescriptionControl = this.fb.control(null, Validators.compose([Validators.required, Validators.maxLength(800)]));
    this.currentUserId = this.ctxDS.peekCurrentUserId();
  }

  ngOnInit(): void {
    this.subscription$.add(
      this.refId$
        .pipe(switchMap((refId) => this.noteService.getNotes$(this.type, refId)))
        .subscribe((notes) => (this.notes = notes.sort((a, b) => b.lastUpdate - a.lastUpdate)))
    );

    this.usersNameMap = MapHlp.fieldsMap(this.ctxDS.peekEstateAdminUsers(), 'id', (u) => ToString.fullName(u));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.refId) {
      this.refId$.next(changes.refId.currentValue);
    }
  }

  ngOnDestroy(): void {
    this.subscription$.unsubscribe();
  }

  public onCreate(): void {
    this.createMode = true;
    this.noteTitleControl.patchValue(null);
    this.noteDescriptionControl.patchValue(null);
  }

  public onEdit(note: GenericNote, index: number): void {
    this.changedNoteIndex = index;
    this.noteTitleControl.patchValue(note.title);
    this.noteDescriptionControl.patchValue(note.description);
  }

  public get noteDescriptionValue(): string {
    return this.noteDescriptionControl.value?.trim() ?? '';
  }

  public async onSave(): Promise<void> {
    if (
      (!this.contentOnly && this.noteTitleControl.invalid) ||
      this.noteDescriptionControl.invalid ||
      !this.noteDescriptionValue ||
      (!this.createMode && this.changedNoteIndex == -1)
    ) {
      return;
    }
    const noteDataOld = this.notes[this.changedNoteIndex] ?? null;
    const description = this.noteDescriptionValue;
    const currentUser = this.ctxDS.peekCurrentUser();
    const newNoteData: GenericNote = {
      id: noteDataOld?.id ?? this.pushId.create(),
      title: this.contentOnly ? description.slice(0, 100) : this.noteTitleControl.value,
      description,
      lastUpdate: Date.now(),
      refId: this.refId,
      userId: this.currentUserId,
      authorName:
        noteDataOld?.authorName ||
        ToString.fullName(currentUser) ||
        currentUser?.email ||
        this.currentUserId ||
        this.trans('label.system')
    };
    try {
      await this.noteService.setNote(this.type, newNoteData);
      this.snackBarService.showSuccess('success.noteAdd');
      this.onCancel();
    } catch (e) {
      this.errorHandlingService.showGlobalError('error.noteAdd', e);
    }
  }

  public onCancel(): void {
    this.createMode = false;
    this.changedNoteIndex = -1;
  }

  public onDelete(note: GenericNote): void {
    this.dialogService
      .openConfirmationDialog({
        text: `${this.trans('note.confirmation')} '${note.title}'?`,
        acceptKey: 'label.yes',
        cancelKey: 'label.no',
        colors: this.type === NoteType.OFFER_MEMBER ? ColorSchemes.OFFERS : ColorSchemes.PRIMARY
      })
      .subscribe(async (response) => {
        if (response) {
          try {
            await this.noteService.removeNote(this.type, this.refId, note.id);
            this.snackBarService.showSuccess('success.noteDelete');
          } catch (e) {
            this.errorHandlingService.showGlobalError('error.noteDelete', e);
          }
        }
      });
  }

  public getUserData(note: GenericNote): string {
    return note.authorName?.trim() || this.usersNameMap.get(note.userId ?? '') || this.trans('label.system');
  }
}
